import React from 'react';
import { Box, Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { OrderData } from '../../../../../api';
import { useEditOrder } from '../../EditOrderContext';
import { PriceMode } from '../../../../../common/OrderUtils';

enum EditOrderDialogIntent {
  CreateOrder = 'CreateOrder',
  EditOrder = 'EditOrder',
  RedeemVoucher = 'RedeemVoucher',
  CreateVoucher = 'CreateVoucher',
  EditVoucher = 'EditVoucher',
  CloneOrder = 'CloneOrder',
  ConvertWaitListItem = 'ConvertWaitListItem',
  RedeemPass = 'RedeemPass',
}

const getButtonText = (editOrderDialogIntent: EditOrderDialogIntent, t: any): string => {
  switch (editOrderDialogIntent) {
    case EditOrderDialogIntent.CreateVoucher:
      return t('vouchers.create');
    case EditOrderDialogIntent.EditVoucher:
    case EditOrderDialogIntent.EditOrder:
      return t('save_changes');
    case EditOrderDialogIntent.RedeemVoucher:
      return t('calendar.edit_order.redeemvoucher');
    case EditOrderDialogIntent.CloneOrder:
      return t('calendar.edit_order.cloneOrder');
    case EditOrderDialogIntent.RedeemPass:
      return t('passes.redeem_pass');
    case EditOrderDialogIntent.CreateOrder:
      return t('calendar.edit_order.create_order_cta');
    default:
      return t('create_order');
  }
};

type EditOrderDialogFooterProps = {
  editOrderDialogIntent: EditOrderDialogIntent;
  loading: boolean;
  order?: Partial<OrderData>;
  isEditOrder: boolean;
  onConvertToVoucher: () => void;
  voucherDialogs?: { dialogNodes?: React.ReactNode };
  extraError?: string;
};

export const EditOrderDialogFooter = ({
  editOrderDialogIntent,
  loading,
  order,
  isEditOrder,
  onConvertToVoucher,
  voucherDialogs,
  extraError,
}: EditOrderDialogFooterProps) => {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { values, setFieldValue, submitForm, errors, submitCount } = useFormikContext<any>();
  const firstError = extraError ?? (submitCount > 0 ? (Object.values(errors)[0] as string | undefined) : undefined);
  // @ts-ignore
  const { editOrderState } = useEditOrder();
  const showZeroPriceNote =
    editOrderDialogIntent === EditOrderDialogIntent.CreateOrder && editOrderState.priceMode === PriceMode.ZERO;

  const noticeSx = { borderRadius: '6px', p: '10px 14px', fontSize: 14 };

  return (
    <Grid
      container
      direction="column"
      flexWrap="nowrap"
      gap={1.5}
      sx={{
        position: 'relative',
        flexShrink: 0,
        width: 'auto',
        p: { xs: '12px 12px 16px', md: '16px 24px 22px' },
        mx: { xs: '-12px', md: '-24px' },
        borderTop: '1px solid',
        borderColor: 'mb.lineSoft',
      }}>
      {isEditOrder && (
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            insetInline: 0,
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'mb.lineSoft',
            paddingInline: { xs: '12px', md: '24px' },
            py: 1,
            zIndex: 1,
          }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={values.skipCustomerEditNotifications}
                onChange={(e) => setFieldValue('skipCustomerEditNotifications', e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                {t('calendar.edit_order.skip_customer_notifications')}
              </Typography>
            }
          />
        </Box>
      )}
      {showZeroPriceNote && !firstError && (
        <Grid
          container
          alignItems="center"
          wrap="nowrap"
          gap={1}
          sx={{ ...noticeSx, bgcolor: 'mb.claySoft', color: 'mb.clayInk' }}>
          <WarningAmberOutlined sx={{ fontSize: 18 }} />
          {t('calendar.edit_order.zero_price_note')}
        </Grid>
      )}
      {firstError && (
        <Grid
          container
          alignItems="center"
          wrap="nowrap"
          gap={1}
          sx={{ ...noticeSx, bgcolor: 'error.light', color: 'error.main' }}>
          <WarningAmberOutlined sx={{ fontSize: 18 }} />
          {firstError}
        </Grid>
      )}
      <Grid container justifyContent={isEditOrder ? 'space-between' : 'flex-end'} alignItems="center" gap={1.5}>
        {isEditOrder && (
          <LoadingButton
            disabled={order?.status === 'archived'}
            type="button"
            color="primary"
            loading={loading}
            variant="outlined"
            onClick={onConvertToVoucher}
            sx={{ textTransform: 'none' }}>
            {t('vouchers.convertDialog.convertButton')}
          </LoadingButton>
        )}
        <LoadingButton
          style={{ textTransform: 'none' }}
          type="submit"
          color="primary"
          loading={loading}
          variant="contained"
          fullWidth={!isEditOrder}
          size={isEditOrder ? 'medium' : 'large'}
          onClick={() => submitForm()}>
          {getButtonText(editOrderDialogIntent, t)}
        </LoadingButton>
      </Grid>
      {voucherDialogs?.dialogNodes}
    </Grid>
  );
};

export { EditOrderDialogIntent, getButtonText };
